import React, { useCallback, useContext, useEffect, useState } from 'react';
import MTable from '../../../../Components/MTable/MTable';
import { useIntl } from 'react-intl';
import { useAptorApi } from '../../../../Api';
import { MTableToolbar, Query } from '@material-table/core';
import { IGridQueryRequest, OrderDirection } from '../../../../Api/AptorApi';
import { ChangeFilter } from './Filters';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useIsMounted } from '../../../../Hooks/useIsMounted';
import moment from 'moment';
import { useFilter } from '../../../../Components/ListFilter';
import { Button, Tooltip } from '@material-ui/core'
import { ArrowUpward, SaveAlt } from '@material-ui/icons'
import { useStyles } from "../../LawPortal.styles";
import { ModuleContext } from "../../../../Context/ModuleContext/ModuleContext";

type UnacknowledgedChange = {
  id: number;
  number: string;
  effectiveFrom: Date;
  reqId: number;
  name: string;
  userId: number;
  userName: string;
  type: 'Law' | 'Requirement';
};

export const ChangesToAcknowledge = () => {
  const { formatMessage } = useIntl();
  const { api } = useAptorApi();
  const [filters, setFilter] = useFilter();
  const tableRef = React.useRef<any>(null);
  const isMounted = useIsMounted();
  const classes = useStyles();
  const { setHeader } = useContext(ModuleContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);

  useEffect(() => {
    if (isMounted && isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, tableRef, isMounted, isFiltersInitialized]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParamsMap = {
      companyUnit: 'companyUnit',
      complianceUsers: 'complianceUsers',
      category: 'category',
      area: 'area',
    };
    Object.entries(queryParamsMap).forEach(([queryKey, stateKey]) => {
      const queryValues = searchParams.getAll(queryKey);
      if (queryValues.length) {
        setFilter(stateKey, queryValues.join(',').split(',').map(num => num.toString()));
      }
    });
    setIsFiltersInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // setHeader(formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }), [
    //   {
    //     title: formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }),
    //     to: `/law-portal/other-requirements`,
    //   },
    //   {
    //     title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.unacknowledged' }),
    //     to: `/law-portal/other-requirements/unacknowledged`,
    //   },
    //   {
    //     title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.acknowledged' }),
    //     to: `/law-portal/other-requirements/acknowledged`,
    //   },
    // ]);
     setHeader(formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }));
  }, [setHeader, formatMessage]);

  const handleDataFetch = useCallback(
    async (query: Query<UnacknowledgedChange>) => {
      if (!isFiltersInitialized) {
        return { data: [], page: 0, totalCount: 0 };
      }
      return await api.searchrequirementsUnacknowledgedChanges<UnacknowledgedChange>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'effectiveFrom',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
    },
    [api, filters, isFiltersInitialized],
  );

  const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

useEffect(() => {
  const updatedQueryParams = Object.keys(filters)
    .filter(key => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return value.join() !== "";
      } else if (typeof value === 'object' && value !== null) {
        return value.min !== "" && value.max !== "";
      }
      return false;
    })
    .map(key => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return `${key}=${encodeURIComponent(value.join())}`;
      } else if (typeof value === 'object' && value !== null) {
        return `${key}[min]=${encodeURIComponent(value.min)}&${key}[max]=${encodeURIComponent(value.max)}`;
      }
      return ''; // Fallback return to satisfy ESLint
    })
    .filter(param => param !== '') // Remove any empty strings
    .join('&');

  navigate({
    pathname: location.pathname,
    search: `?${updatedQueryParams}`,
  });
}, [filters, navigate, location.pathname]);

  const handleExport = async () => {
    const response = await api.exportrequirementsUnacknowledgedChanges();
    const blob = await response?.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = 'RequirementExport.xlsx';
    if (contentDisposition && contentDisposition.includes('attachment')) {
      const matches = /filename=([^;]*)/.exec(contentDisposition);
      if (matches && matches[1]) {
        fileName = matches[1];
      }
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <ChangeFilter filters={filters} setFilter={setFilter} />
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <MTable
        tableRef={tableRef}
        columns={[
          {
            title: formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }),
            field: 'name',
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.number' }),
            field: 'number',
            render: (row) => (
              <Link to={`/law-portal/other-requirements/${row.reqId}/changes`}>
                {row.number}
              </Link>
            ),
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.effectiveFrom' }),
            field: 'effectiveFrom',
            render: (row) => renderDate(row.effectiveFrom),
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.user' }),
            field: 'userName',
          },
        ]}
        data={handleDataFetch}
        components={{
          Toolbar: (props) => (
            <div className={classes.toolbar}>
              <MTableToolbar {...props} />
              <SaveAlt style={{ cursor: 'pointer' }} onClick={() => handleExport()} />
            </div>
          ),
        }}
      />
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
    </>
  );
};